import { Settings } from 'lucide-react';

import { useObjectUrl } from '../../hooks/useObjectUrl';
import { cn } from '../../lib/utils';
import { MEDIA_FADE_IN_CLASS, revealMedia } from '../Form';
import { S3Image } from '../S3Image';

interface EditorsChoicePreviewProps {
  dappName: string;
  subtitle: string;
  headline: string;
  tag: string;
  iconFile?: File | null;
  existingIconUrl?: string | null;
  graphicFile?: File | null;
  existingGraphicUrl?: string | null;
}

function PreviewImage({
  file,
  existingUrl,
  alt,
}: {
  file?: File | null;
  existingUrl?: string | null;
  alt: string;
}) {
  const fileUrl = useObjectUrl(file ?? null);
  if (fileUrl) {
    return (
      <img
        src={fileUrl}
        alt={alt}
        onLoad={revealMedia}
        onError={revealMedia}
        className={MEDIA_FADE_IN_CLASS}
      />
    );
  }
  if (existingUrl) {
    return (
      <S3Image
        src={existingUrl}
        alt={alt}
        onLoad={revealMedia}
        onError={revealMedia}
        className={MEDIA_FADE_IN_CLASS}
      />
    );
  }
  return null;
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-2">
      <div className="size-8 shrink-0 rounded-full bg-seeker-200" />
      <div className="flex flex-1 flex-col gap-1.5">
        <div className="h-2 w-24 rounded-full bg-seeker-200" />
        <div className="h-2 w-28 rounded-full bg-seeker-200" />
      </div>
      <div className="h-5 w-14 rounded-full bg-seeker-200" />
      <div className="h-8 w-3 rounded-l-full bg-seeker-200" />
    </div>
  );
}

/** White phone mock of the dApp Store home with the featured carousel module. */
export function EditorsChoicePreview({
  dappName,
  subtitle,
  headline,
  tag,
  iconFile,
  existingIconUrl,
  graphicFile,
  existingGraphicUrl,
}: EditorsChoicePreviewProps) {
  const hasGraphic = Boolean(graphicFile || existingGraphicUrl);
  const hasIcon = Boolean(iconFile || existingIconUrl);
  const hasContent = hasGraphic || Boolean(headline || tag);

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="text-sm text-white">Editor&apos;s Choice Preview</div>
      <div className="flex w-[300px] flex-col gap-4 overflow-hidden rounded-[30px] bg-white px-3 pb-6 pt-6 text-black">
        <div className="flex items-center justify-between px-1">
          <span className="text-xl font-medium leading-6">
            Solana dApp Store
          </span>
          <Settings className="size-5" aria-hidden="true" />
        </div>

        <div className="flex gap-2">
          <div
            className={cn(
              'flex w-[264px] shrink-0 flex-col overflow-hidden rounded-2xl',
              hasContent ? 'border border-seeker-200' : 'bg-seeker-200',
            )}
          >
            <div className="relative aspect-square w-full overflow-hidden bg-seeker-200 [&_img]:size-full [&_img]:object-cover">
              {hasGraphic ? (
                <PreviewImage
                  file={graphicFile}
                  existingUrl={existingGraphicUrl}
                  alt="Editor's Choice graphic preview"
                />
              ) : null}
              {hasContent ? (
                <>
                  <span className="absolute left-3 top-3 rounded-full bg-white px-2.5 py-1 text-[9px] font-bold uppercase leading-3 tracking-wide text-black">
                    {tag || 'Featured'}
                  </span>
                  {headline ? (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-3 pt-10">
                      <p className="line-clamp-2 text-xl font-medium leading-6 text-white">
                        {headline}
                      </p>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="flex h-14 items-center gap-2 px-3">
              <div
                className={cn(
                  'size-8 shrink-0 overflow-hidden rounded-full [&_img]:size-full [&_img]:object-cover',
                  hasIcon && hasContent ? 'bg-transparent' : 'bg-seeker-200',
                )}
              >
                {hasContent && hasIcon ? (
                  <PreviewImage
                    file={iconFile}
                    existingUrl={existingIconUrl}
                    alt="App icon preview"
                  />
                ) : null}
              </div>
              {hasContent ? (
                <div className="flex min-w-0 flex-1 flex-col">
                  <span
                    className="truncate text-sm font-medium"
                    title={dappName || undefined}
                  >
                    {dappName || 'Your app'}
                  </span>
                  <span
                    className="truncate text-[10px] leading-3 text-zinc-500"
                    title={subtitle || undefined}
                  >
                    {subtitle}
                  </span>
                </div>
              ) : (
                <div className="h-2 flex-1 rounded-full bg-seeker-200" />
              )}
              {hasContent ? (
                <span className="rounded-full bg-black px-3 py-1 text-xs font-bold text-white">
                  Install
                </span>
              ) : (
                <div className="h-5 w-14 rounded-full bg-seeker-200" />
              )}
            </div>
          </div>
          <div className="w-3 shrink-0 rounded-l-2xl bg-seeker-200" />
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <div className="h-6 w-20 rounded bg-seeker-200" />
          <SkeletonRow />
          <SkeletonRow />
          <div className="h-6 w-28 rounded bg-seeker-200" />
          <SkeletonRow />
        </div>
      </div>
    </div>
  );
}
